import { LoaderFunction, redirect, useLoaderData } from "react-router-dom";

import { ReferralRewardsContainer } from "@/modules/referralRewards";
import { queryClient } from "@/pages/Root";
import {
  getIncentivesByPlatformIdReferralAchievementsByPersonId,
  getIncentivesByPlatformIdReferralHistoryByPersonId,
  getIncentivesByPlatformLocationIdAvailableReferralReward,
  getPlatformCurrent,
  getUser,
} from "api/methods";
import { waitObj } from "toolbox/Promise";

const load = async () => {
  const platform = await queryClient.fetchQuery({ ...getPlatformCurrent.keyed() });

  const userInfo = await queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const referralRewardForPlatformLocation = queryClient
    .fetchQuery({
      ...getIncentivesByPlatformLocationIdAvailableReferralReward.keyed({
        platformLocationId: userInfo?.platformLocation?.id ?? "",
      }),
    })
    .catch(() => null);

  const referralHistoryForPerson = queryClient
    .fetchQuery({
      ...getIncentivesByPlatformIdReferralHistoryByPersonId.keyed({
        platformId: platform?.id ?? "",
        personId: userInfo?.id ?? "",
      }),
    })
    .catch(() => null);

  const referralAchievementsForPerson = queryClient
    .fetchQuery({
      ...getIncentivesByPlatformIdReferralAchievementsByPersonId.keyed({
        platformId: platform?.id ?? "",
        personId: userInfo?.id ?? "",
      }),
    })
    .catch(() => null);

  return waitObj({
    userInfo,
    platform,
    referralReward: referralRewardForPlatformLocation,
    referralHistoryForPerson,
    referralAchievementsForPerson,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const referralRewardsPageLoader: LoaderFunction = async () => {
  const data = await load();

  if (!data.platform?.platformSettings?.rewardsEnabled) {
    return redirect("/home");
  }

  return data;
};

export default function ReferralRewardsPage() {
  const { platform, referralReward, referralHistoryForPerson, referralAchievementsForPerson } =
    useLoaderData() as LoaderData;

  return (
    <ReferralRewardsContainer
      platform={platform}
      referralReward={referralReward!}
      referralHistoryForPerson={referralHistoryForPerson!}
      achievementProgress={referralAchievementsForPerson!}
    />
  );
}
